import React, { Component, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { ButtonGroup } from '@atlaskit/button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	type KeyboardOrMouseEvent,
	ModalTransition,
	ModalHeader,
	ModalTitle,
	ModalBody,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type {
	IntlShapeV2 as IntlShape,
	MessageDescriptorV2 as MessageDescriptor,
} from '@atlassian/jira-intl/src/v2/types.tsx';
import { FireScreenAnalytics, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { HistoryBlocker } from '@atlassian/react-resource-router';
import commonMessages from '../../../i18n';
import type { BeforeNavigateCallbackLegacy } from '../../bridge';
import messages from './messages';

export type ContinueNavigation = () => void;
export type BeforeNavigateCallback = (event: BeforeUnloadEvent) => string | undefined;

type Props = {
	prompt: MessageDescriptor;
	onRegisterLegacyBlock?: (arg1: BeforeNavigateCallbackLegacy) => () => void;
	onRegisterBlock?: (arg1: HistoryBlocker) => () => void;
	onSave: (arg1: () => void, arg2: UIAnalyticsEvent) => void;
	onDiscard: () => void;
	onUnload: (cb: BeforeNavigateCallback) => () => void;
	onConfirmNavigation: () => void;
	onRejectNavigation: () => void;
	withSaveButton: boolean;
	isDirty: boolean;
	fireOnSaveAnalytics: boolean;
	intl: IntlShape;
};

type State = {
	showPrompt: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
class NavigationPrompt extends Component<Props, State> {
	static defaultProps: Partial<Props> = {
		onRejectNavigation: noop,
		onConfirmNavigation: noop,
	};

	state = {
		showPrompt: false,
	};

	componentDidMount() {
		this.registerOnUnloadHandler();

		if (__SPA__) {
			this.registerSpaNavigationHandler();
		} else {
			this.registerLegacyNavigationHandler();
		}
	}

	componentWillUnmount() {
		this.unregisterBlock && this.unregisterBlock();
		this.unregisterOnUnload && this.unregisterOnUnload();
	}

	onSave = (_: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		if (this.props.fireOnSaveAnalytics) {
			fireUIAnalytics(analyticsEvent, 'save');
		}

		this.setState({ showPrompt: false });
		this.props.onSave(this.continueNavigation, analyticsEvent);
	};

	onClose = (_: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'close');
		this.setState({ showPrompt: false });
		this.navigationResolver && this.navigationResolver(false);
		this.props.onRejectNavigation();
	};

	onDiscard = (_: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'discard');
		this.setState({ showPrompt: false });
		this.continueNavigation();
		// To prevent a scenario whereby the user is on new queues, clicks the new queue button
		// and we re-render the new queues page and re-mount the blocker.
		// If we discard too quickly, the blocker remounts and discards again before the re-render.
		setTimeout(() => this.props.onDiscard());
	};

	continueNavigation = () => {
		this.unregisterBlock && this.unregisterBlock();
		this.props.onConfirmNavigation();
		this.navigationResolver && this.navigationResolver(true);
	};

	registerOnUnloadHandler() {
		const { onUnload } = this.props;
		if (!onUnload) {
			return;
		}

		const beforeNavigateCallback = (e: BeforeUnloadEvent) => {
			if (!this.props.isDirty) {
				return;
			}

			e.preventDefault(); // Firefox
			// Return value is not shown to user but needs to be not null or undefined for the confirm page unload prompt to be shown
			// https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event#compatibility_notes
			e.returnValue = ''; // Chrome
			return ''; // IE
		};

		this.unregisterOnUnload = onUnload(beforeNavigateCallback);
	}

	registerLegacyNavigationHandler() {
		const { onRegisterLegacyBlock } = this.props;
		if (onRegisterLegacyBlock) {
			this.unregisterBlock = onRegisterLegacyBlock(() => {
				if (this.props.isDirty) {
					this.setState({
						showPrompt: true,
					});
				} else {
					this.continueNavigation();
				}
			});
		}
	}

	registerSpaNavigationHandler() {
		const { onRegisterBlock } = this.props;
		if (onRegisterBlock) {
			this.unregisterBlock = onRegisterBlock(() => {
				if (!this.props.isDirty) {
					return Promise.resolve(true);
				}
				return new Promise((resolve: (result: Promise<boolean> | boolean) => void) => {
					this.setState({
						showPrompt: true,
					});
					this.navigationResolver = resolve;
				});
			});
		}
	}

	unregisterBlock: (() => void) | undefined;

	unregisterOnUnload: (() => void) | undefined;

	navigationResolver: ((arg1: Promise<boolean> | boolean) => void) | undefined;

	renderFooter = () => {
		const {
			intl: { formatMessage },
			withSaveButton,
		} = this.props;

		return (
			<Container>
				<ButtonGroup>
					{withSaveButton ? (
						<>
							<Button appearance="primary" onClick={this.onSave}>
								{formatMessage(commonMessages.save)}
							</Button>
							<Button appearance="subtle-link" onClick={this.onDiscard}>
								{formatMessage(commonMessages.discard)}
							</Button>
						</>
					) : (
						<Button appearance="primary" onClick={this.onDiscard}>
							{formatMessage(messages.leave)}
						</Button>
					)}
					<Button appearance="link" onClick={this.onClose}>
						{formatMessage(commonMessages.cancel)}
					</Button>
				</ButtonGroup>
			</Container>
		);
	};

	render() {
		const { showPrompt } = this.state;
		const {
			intl: { formatMessage },
			prompt,
		} = this.props;
		if (!showPrompt) {
			return null;
		}
		return (
			<ModalTransition>
				<ShortcutScope>
					<ModalDialog onClose={this.onClose}>
						<ModalHeader>
							<ModalTitle>{formatMessage(messages.unsavedChangesModalTitle)}</ModalTitle>
						</ModalHeader>

						<ModalBody>
							{formatMessage(prompt, {
								b: (...parts: ReactNode[]) => <Text as="strong">{parts}</Text>,
							})}
							<FireScreenAnalytics />
						</ModalBody>

						<ModalFooter>{this.renderFooter()}</ModalFooter>
					</ModalDialog>
				</ShortcutScope>
			</ModalTransition>
		);
	}
}

export default injectIntl(NavigationPrompt);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{}>({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
});
